import _ from "lodash";
import React from "react";
import classes from "./WorksheetTextView.scss";
import { connect } from "react-redux";
import { compose } from "lib/apolloClient";
import { CloseCircleFilled, TickCircleFilled } from "@toddle-edu/ds-icons";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import classNames from "classnames";
import TheatreModeV2 from "UIComponents/TheatreModeV2";
import {
  getTypeDetailsFromUrl,
  extractMediaAttachments,
  getTestCloudUrls,
  replaceUnsignedUrls,
  normalizeUrl,
  extractTextWithLatex,
} from "Worksheet/modules/WorksheetUtils";
import { getIsWorksheetVideoLagFixEnabled } from "Worksheet/modules/WorksheetFeatureFlags";
import { getStringAutoLinks, getStyleStrippedText } from "Utils";
import { getSignedUrls } from "Worksheet/modules/WorksheetModules";
import DocEditor from "UIComponents/DocEditor";
import I18nHOC from "UIComponents/I18nHOC";
import MiniDocEditor from "UIComponents/DocEditor/MiniDocEditorWrapper";
import {
  DOC_EDITOR_STYLES,
  FIELD_UID_BLANKS,
  DOC_EDITOR_OVERRIDE_ELEMENTS,
} from "Worksheet/modules/WorksheetConstants";

const FLOATING_TOOLBAR_CONFIG = { isEnabled: true };

class WorksheetTextView extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.textContainer = null;
    this.contentCache = null;
    this.state = {
      showMediaModal: false,
      mediaViewerData: {},
    };
  }

  setTextContainerRef = ref => {
    this.textContainer = ref;
  };

  attachContainerListeners = () => {
    if (!this.textContainer) return;
    this.textContainer.setAttribute("dir", "auto");
    this.textContainer.addEventListener("click", this.handleClick);
    this.textContainer.addEventListener("mouseup", this.handleMouseUp);
    this.textContainer.addEventListener("keydown", this.handleKeyDown);
  };

  detachContainerListeners = () => {
    if (!this.textContainer) return;
    this.textContainer.removeEventListener("click", this.handleClick);
    this.textContainer.removeEventListener("mouseup", this.handleMouseUp);
    this.textContainer.removeEventListener("keydown", this.handleKeyDown);
  };

  componentDidMount() {
    this.attachContainerListeners();
    setTimeout(() => {
      this.addIframeOverlays();
    }, 1000);
  }

  componentWillUnmount() {
    super.componentWillUnmount();
    this.detachContainerListeners();
  }

  // Blank to be rendered in the evaluate mode
  renderEvaluationBlank = params => {
    const { value, answerBlock, answerArray, t } = params;

    const isCorrect = _.includes(
      _.map(answerArray, item => _.toLower(extractTextWithLatex(item))),
      _.toLower(extractTextWithLatex(value))
    );

    const studentResponseClasses = classNames(
      classes.studentResponse,
      { [classes.correct]: isCorrect },
      { [classes.wrong]: !isCorrect },
      { [classes.empty]: _.isEmpty(value) }
    );

    return (
      <span className={classes.evaluationContainer}>
        <div className={studentResponseClasses}>
          {isCorrect ? (
            <TickCircleFilled variant={"success"} />
          ) : !_.isEmpty(value) ? (
            <CloseCircleFilled variant={"critical"} />
          ) : (
            t("common:no_with_label", { label: t("common:response") })
          )}
          {this.isHTML(value) ? (
            <div
              className={classes.studentResponseText}
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: value,
                  type: "DOCUMENT_EDITOR",
                }),
              }}
            />
          ) : (
            value
          )}
        </div>
        {!_.isEmpty(answerBlock) && !isCorrect ? (
          <span
            className={classes.answerBlock}
            dangerouslySetInnerHTML={{
              __html: getStyleStrippedText({
                text: answerBlock,
                type: "DOCUMENT_EDITOR",
              }),
            }}
          />
        ) : null}
      </span>
    );
  };

  // Blank to be rendered in the view mode
  renderViewBlank = ({ placeholder, value, mode }) => {
    const tooltipContainerClasses = classNames({
      [classes.tooltipContainer]: true,
      [classes.tooltipContainerView]: _.isEqual(mode, "view"),
    });

    return (
      <div className={tooltipContainerClasses}>
        <div className={classes.docEditorInBlanksViewContainer}>
          <div
            className={classes.docEditorInBlanksView}
            dangerouslySetInnerHTML={{
              __html: getStyleStrippedText({
                text: _.includes(["create", "preview"], mode)
                  ? placeholder
                  : value,
                type: "DOCUMENT_EDITOR",
              }),
            }}
          />
        </div>

        {placeholder && (
          <div className={classes.tooltipText}>
            <div
              dangerouslySetInnerHTML={{
                __html: getStyleStrippedText({
                  text: placeholder,
                  type: "DOCUMENT_EDITOR",
                }),
              }}
            />
          </div>
        )}
      </div>
    );
  };

  // Blank to be rendered in the edit mode
  renderEditorBlank = ({ index, i, value, onInputChange }) => (
    <MiniDocEditor
      key={`blank-${index}-${i}`}
      name={_.toString(index)}
      value={value}
      width="auto"
      minHeight={32}
      saveDelay={300}
      isSlashMenuEnabled
      floatingMiniEditorToolbarConfig={FLOATING_TOOLBAR_CONFIG}
      updateInputField={(val, isEmpty) => {
        onInputChange({
          val: isEmpty ? { [index]: "" } : val,
        });
      }}
      editorClassName={classes.docEditorInBlanks}
      containerStyle={DOC_EDITOR_STYLES.MINI_EDITOR_BLANKS_CONTAINER_STYLES}
      styles={DOC_EDITOR_STYLES.MINI_EDITOR_BLANKS_STYLES}
      singleLine={true}
      isPortalToBody={true}
    />
  );

  // Rendering mini editor for the blank question type
  renderHtmlWithMiniEditor = () => {
    const VOID_ELEMENTS = ["br", "col", "embed", "hr", "img"];
    const { value, fieldMetaData: blanksMeta } = this.props;

    const html = this.removeEmptyLines(value);
    if (!html) return null;

    const doc = new DOMParser().parseFromString(html, "text/html");

    const getProps = (node, key) => {
      const props = { key };

      const parseStyleString = styleStr => {
        if (!styleStr) return {};

        return _.reduce(
          styleStr.split(";"),
          (acc, rule) => {
            if (!rule) return acc;

            const [prop, value] = rule.split(":");
            if (!prop || !value) return acc;

            const camelProp = _.camelCase(prop.trim());
            acc[camelProp] = value.trim();

            return acc;
          },
          {}
        );
      };

      _.forEach(node.attributes, attr => {
        if (attr.name === "class") props.className = attr.value;
        else if (attr.name === "style")
          props.style = parseStyleString(attr.value);
        else props[attr.name] = attr.value;
      });

      return props;
    };

    const renderBlank = (index, i) => {
      const metadata = _.get(blanksMeta, index, {});
      const {
        placeholder,
        mode,
        onInputChange,
        value,
        answerBlock,
        answerArray,
        t,
      } = metadata;

      if (mode === "evaluate") {
        return this.renderEvaluationBlank({
          answerBlock,
          answerArray,
          t,
          value,
        });
      }

      const viewOnly = !_.includes(["edit", "teacher-preview"], mode);

      return viewOnly
        ? this.renderViewBlank({ placeholder, value, mode })
        : this.renderEditorBlank({ index, i, value, onInputChange });
    };

    const renderTextNode = node =>
      _.map(_.split(node.nodeValue, /(__BLANK_\d+__)/g), (part, i) => {
        const match = part.match(/^__BLANK_(\d+)__$/);
        return match ? renderBlank(Number(match[1]), i) : part;
      });

    const walk = (node, key = 0) => {
      if (node.nodeType === 3) return renderTextNode(node);

      if (node.nodeType === 1) {
        const tag = _.toLower(node.tagName);
        const props = getProps(node, key);

        if (_.includes(VOID_ELEMENTS, tag)) {
          return React.createElement(tag, props);
        }

        const children = _.flatMap(node.childNodes, (child, i) =>
          walk(child, `${key}-${i}`)
        );

        const element = React.createElement(tag, props, children);

        if (tag === "table") {
          return React.createElement(
            "div",
            { key, className: "ds-de-table-scrollable-wrapper" },
            element
          );
        }

        return element;
      }

      return null;
    };

    return _.map(doc.body.childNodes, (node, i) => walk(node, i));
  };

  getAttachmentData = async () => {
    const { value, getSignedUrls } = this.props;
    const content = this.isHTML(value) ? this.removeEmptyLines(value) : value;
    const rawAttachments = extractMediaAttachments(content);
    const rawUrls = _.map(rawAttachments, "url");
    // Create a hashmap with key as index and value as url for testcloud.toddleapp.com URLs only
    const urlHashmap = getTestCloudUrls(rawUrls);

    const signedUrls = await getSignedUrls({
      urls: _.values(urlHashmap),
    });

    // Create a new array of URLs, replacing those in the hashmap with their signed versions
    const processedUrls = replaceUnsignedUrls({
      rawAttachments,
      signedUrls,
      urlHashmap,
    });

    return _.reduce(
      processedUrls,
      (attachments, attachment) => {
        const { url, name } = attachment;
        const typeDetails = getTypeDetailsFromUrl(url);

        attachments.push({
          url,
          ...typeDetails,
          name,
        });

        return attachments;
      },
      []
    );
  };

  handleClick = async (event, iframe = null) => {
    const element = event?.target;
    const tagName = _.get(element, "tagName", "").toLowerCase();
    // Handle link clicks to prevent event bubbling
    if (tagName === "a") {
      event.stopPropagation();
    }

    // Only process media viewer logic if enabled
    event.preventDefault();
    let url = null;
    url = element?.closest("a")?.href;

    if (iframe) {
      url = iframe?.src;
    }

    if (!url) return;

    // Get attachment data
    const attachments = await this.getAttachmentData();
    if (!attachments.length) return;

    // Normalize the clicked URL
    const normalizedClickedUrl = normalizeUrl(url);

    // Find matching attachment
    const activeAttachmentIndex = _.findIndex(
      attachments,
      attachment => normalizeUrl(attachment.url) === normalizedClickedUrl
    );

    // If we found a matching attachment, show the media viewer
    if (activeAttachmentIndex !== -1) {
      event.preventDefault();
      this.setState({
        showMediaModal: true,
        mediaViewerData: {
          attachments,
          activeAttachmentIndex,
        },
      });
    }
  };

  handleKeyDown = event => {
    if (
      event.code === "Space" &&
      event.target?.tagName?.toLowerCase() === "video"
    ) {
      event.preventDefault();
      const video = event.target;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }
  };

  handleMouseUp = event => {
    // Handle video clicks to prevent event bubbling
    if (_.get(event, "target.tagName", "").toLowerCase() === "video") {
      event.stopPropagation();
    }
  };

  getProcessedContent = () => {
    const { value, sanitizationType = "DOCUMENT_EDITOR" } = this.props;
    const isWorksheetVideoLagFixEnabled = getIsWorksheetVideoLagFixEnabled();
    if (this.contentCache && this.contentCache.value === value) {
      return this.contentCache.result;
    }
    const content = this.isHTML(value) ? this.removeEmptyLines(value) : value;
    const result = getStyleStrippedText({
      text:
        isWorksheetVideoLagFixEnabled && this.isHTML(content)
          ? content
          : getStringAutoLinks(content),
      type: sanitizationType,
    });
    this.contentCache = { value, result };
    return result;
  };

  isHTML = content => {
    const htmlRegex = /<[a-z][\s\S]*>/i;
    return htmlRegex.test(content);
  };

  removeEmptyLines = htmlContent => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = htmlContent;

    //Each line generated by the editor generates a paragraph with the class tde-paragraph(rte) or ds-de-paragraph(doc-editor).
    //So using the above the selector we are able to select all the lines of the editor in a line by line order
    const paragraphs = tempDiv.querySelectorAll(
      "p.tde-paragraph, p.ds-de-paragraph, p.ds-de-mini-paragraph"
    );

    // Function to recursively trim the text node
    const trimTextNode = ({ node, type }) => {
      const nodeType = _.get(node, "nodeType", "");
      const isFirst = _.isEqual(type, "FIRST");
      if (nodeType === Node.TEXT_NODE) {
        node.textContent = isFirst
          ? _.trimStart(node.textContent)
          : _.trimEnd(node.textContent);
        return true;
      } else if (nodeType === Node.ELEMENT_NODE && node.hasChildNodes()) {
        const childNode = isFirst ? node.firstChild : node.lastChild;
        return trimTextNode({ node: childNode, type });
      }
      return false;
    };

    // Helper function to check if a node should be preserved
    const shouldPreserveNode = node => {
      // Preserve any node with important form elements
      if (node.querySelector("input")) {
        return true;
      }

      // Preserve BlanksInput containers
      if (node.className && node.className.includes("BlanksInput")) {
        return true;
      }

      // Preserve non-empty spans with children
      if (
        node.tagName === "SPAN" &&
        node.hasChildNodes() &&
        node.childNodes.length > 0
      ) {
        return true;
      }

      return false;
    };

    // Iterate from the beginning
    for (let i = 0; i < paragraphs.length; i++) {
      const paragraph = paragraphs[i];

      // Remove <br> and empty tags from the start
      while (
        paragraph.firstChild &&
        (paragraph.firstChild.tagName === "BR" ||
          (paragraph.firstChild.tagName === "SPAN" &&
            !shouldPreserveNode(paragraph.firstChild) &&
            _.isEmpty(paragraph.firstChild.textContent.trim())))
      ) {
        paragraph.removeChild(paragraph.firstChild);
      }

      // If the paragraph has content, stop iterating from the beginning
      if (paragraph.hasChildNodes()) {
        trimTextNode({ node: paragraph.firstChild, type: "FIRST" });
        break;
      }
    }

    // Iterate from the end
    for (let j = paragraphs.length - 1; j >= 0; j--) {
      const paragraph = paragraphs[j];

      // Remove <br> and empty tags from the end
      while (
        paragraph.lastChild &&
        (paragraph.lastChild.tagName === "BR" ||
          (paragraph.lastChild.tagName === "SPAN" &&
            !shouldPreserveNode(paragraph.lastChild) &&
            _.isEmpty(paragraph.lastChild.textContent.trim())))
      ) {
        paragraph.removeChild(paragraph.lastChild);
      }

      // If the paragraph has content, stop iterating from the end
      if (paragraph.hasChildNodes()) {
        trimTextNode({ node: paragraph.lastChild, type: "LAST" });
        break;
      }
    }

    // Remove paragraphs with no children
    _.forEach(paragraphs, paragraph => {
      if (!paragraph.hasChildNodes()) {
        paragraph.parentNode.removeChild(paragraph);
      }
    });
    return tempDiv.innerHTML;
  };

  toggleMediaModal = () => {
    this.setState({ showMediaModal: false, mediaViewerData: {} });
  };

  addIframeOverlays = () => {
    if (!this.textContainer) return;

    // Remove existing iframe wrappers
    const existingWrappers = this.textContainer.querySelectorAll(
      ".iframe-wrapper"
    );
    _.forEach(existingWrappers, wrapper => {
      const iframe = wrapper.querySelector("iframe");
      if (iframe) wrapper.replaceWith(iframe);
    });

    const iframes = this.textContainer.querySelectorAll('iframe[src*=".pdf"]');

    _.forEach(iframes, iframe => {
      // Create wrapper
      const wrapper = document.createElement("div");
      wrapper.className = "iframe-wrapper";
      wrapper.style.cssText = `
        position: relative;
        display: grid;
      `;

      // Insert wrapper before iframe and move iframe inside
      iframe?.parentNode?.insertBefore(wrapper, iframe);
      wrapper.appendChild(iframe);

      // SVG icon container
      const icon = document.createElement("div");
      icon.style.cssText = `
        position: absolute;
        bottom: 8px;
        left: 12px;
        z-index: 2;
        cursor: pointer;
        border: 1px solid black;
        box-shadow: white 0px 0px 0px 2px;
        display: grid;
        height: 16px;
        width: 16px;
      `;

      icon.innerHTML = `
        <svg viewBox="0 0 100 100"
          class="fill-current w-5 h-5 text-iconDefault"
          aria-hidden="true">
          <path d="M36.585 32.165a3.125 3.125 0 11-4.42 4.42L18.75 23.169v10.313a3.125 3.125 0 11-6.25 0V15.625c0-1.726 1.4-3.125 3.125-3.125h17.857a3.125 3.125 0 110 6.25H23.17l13.416 13.415zM36.585 67.835a3.125 3.125 0 10-4.42-4.42L18.75 76.831V66.518a3.125 3.125 0 10-6.25 0v17.857c0 1.726 1.4 3.125 3.125 3.125h17.857a3.125 3.125 0 100-6.25H23.17l13.416-13.415zM63.415 63.415a3.125 3.125 0 000 4.42L76.831 81.25H66.518a3.125 3.125 0 100 6.25h17.857c1.726 0 3.125-1.4 3.125-3.125V66.518a3.125 3.125 0 10-6.25 0V76.83L67.835 63.415a3.125 3.125 0 00-4.42 0zM63.415 32.165a3.125 3.125 0 104.42 4.42L81.25 23.169v10.313a3.125 3.125 0 106.25 0V15.625c0-1.726-1.4-3.125-3.125-3.125H66.518a3.125 3.125 0 100 6.25H76.83L63.415 32.165z"></path>
        </svg>
      `;

      icon.querySelector("svg").style.cssText = `
        width: 100%;
        height: 100%;
      `;

      icon.addEventListener("click", event => {
        event.preventDefault();
        event.stopPropagation();
        this.handleClick(event, iframe);
      });

      wrapper.appendChild(icon);
    });
  };

  renderView = () => {
    const {
      value,
      customTextStyle,
      applyDefaultClass = true,
      isWordWrapRequired = false, // This prop is required for word wrap for text area elements
      isRtlAlignmentRequired = false,
      dsVersion,
      isInactiveQuestionRevamp = false,
      showDocEditor = false,
      fieldMetaData = {},
      fieldKey,
    } = this.props;
    const isDSV2 = dsVersion === "2.0";

    const { showMediaModal, mediaViewerData } = this.state;
    const { attachments, activeAttachmentIndex } = mediaViewerData;

    const richTextClasses = classNames({
      [classes.richText]: applyDefaultClass,
      [classes.customRichText]: !applyDefaultClass,
      [classes.whiteSpacePreWrap]: isWordWrapRequired,
      [classes.richTextAlignmentByDirAttr]: isRtlAlignmentRequired,
      [classes.inactiveRichTextV2]: isInactiveQuestionRevamp,
      [classes.docEditorCustom]: !applyDefaultClass && showDocEditor,
    });

    if (showDocEditor) {
      return (
        <>
          <div
            ref={this.setTextContainerRef}
            className={richTextClasses}
            style={customTextStyle}
          >
            <DocEditor
              value={this.getProcessedContent()}
              viewOnly={true}
              isCompact={true}
              compactVariantMinHeight={"fit-content"}
              containerStyle={DOC_EDITOR_STYLES.CONTAINER_STYLES}
              compactVariantStyles={
                DOC_EDITOR_STYLES.COMPACT_VARIANT_STYLES_VIEW
              }
              overrideElements={DOC_EDITOR_OVERRIDE_ELEMENTS}
            />
          </div>
          {showMediaModal && (
            <TheatreModeV2
              mediaAttachments={{
                attachments,
              }}
              activeAttachmentIndex={activeAttachmentIndex}
              toggleTheatreMode={this.toggleMediaModal}
            />
          )}
        </>
      );
    }

    const isBlanks = _.includes(fieldKey, FIELD_UID_BLANKS);

    return (
      <>
        <div
          className={classNames({
            [classes.detailText]: !isDSV2,
            ["text-body-1"]: !isDSV2,
            "text-body": isDSV2,
            [classes.detailTextV2]: isDSV2,
            [classes.blanksText]: isBlanks && !_.isEmpty(fieldMetaData),
          })}
          style={customTextStyle}
          ref={this.setTextContainerRef}
        >
          {isBlanks && !_.isEmpty(fieldMetaData) ? (
            this.renderHtmlWithMiniEditor()
          ) : Array.isArray(value) ? (
            _.join(value, ", ")
          ) : (
            <div
              className={richTextClasses}
              dangerouslySetInnerHTML={{
                __html: this.getProcessedContent(),
              }}
            />
          )}
        </div>
        {showMediaModal && (
          <TheatreModeV2
            mediaAttachments={{
              attachments,
            }}
            activeAttachmentIndex={activeAttachmentIndex}
            toggleTheatreMode={this.toggleMediaModal}
          />
        )}
      </>
    );
  };
}

const mapActionCreators = { getSignedUrls };

export default compose(
  I18nHOC({ resource: ["unitPlan"] }),
  connect(null, mapActionCreators)
)(WorksheetTextView);
